import {call, put, takeLatest, all} from 'redux-saga/effects';
import {
  getCategoryListRequest,
  getCategoryListSuccess,
  getCategoryListFailure,
  addCategoryRequest,
  addCategorySuccess,
  addCategoryFailure,
  editCategoryRequest,
  editCategorySuccess,
  editCategoryFailure,
  getFilterOptionsRequest,
  getFilterOptionsSuccess,
  getFilterOptionsFailure,
  addFilterRequest,
  addFilterSuccess,
  addFilterFailure,
  editFilterRequest,
  editFilterSuccess,
  editFilterFailure,
  // get dashboard consultations
  getDashboardConsultationsRequest,
  getDashboardConsultationsSuccess,
  getDashboardConsultationsFailure,
  getPopularSpecialistListRequest,
  getPopularSpecialistListSuccess,
  getPopularSpecialistListFailure,
  getPopularityLimitRequest,
  getPopularityLimitSuccess,
  getPopularityLimitFailure,
  updatePopularityLimitRequest,
  updatePopularityLimitSuccess,
  updatePopularityLimitFailure,
  overrideSpecialistPopularityRequest,
  overrideSpecialistPopularitySuccess,
  overrideSpecialistPopularityFailure,
  getPatientAccountTicketsRequest,
  getPatientAccountTicketsSuccess,
  getPatientAccountTicketsFailure,
  exportPatientAccountRequest,
  exportPatientAccountSuccess,
  exportPatientAccountFailure,
  deletePatientAccountRequest,
  deletePatientAccountSuccess,
  deletePatientAccountFailure,
  holdPatientAccountDeletionRequest,
  holdPatientAccountDeletionSuccess,
  holdPatientAccountDeletionFailure,
  resumePatientAccountDeletionRequest,
  resumePatientAccountDeletionSuccess,
  resumePatientAccountDeletionFailure,
} from '../slices/patientAdminSlice';

import {getCategoryList, addCategory, editCategory, getFilterList, addFilter, editFilter, getAdminPatientConsultations, getPopularSpecialistList, getPopularityLimit, updatePopularityLimit, overrideSpecialistPopularity, getPatientAccountTickets, exportPatientAccount, deletePatientAccount, holdPatientAccountDeletion, resumePatientAccountDeletion} from '@/services/api';
import {SUCCESS_KEY,FilterType} from '@/constants';

function* getCategoryListSaga(action: any): Generator {
  try {
    const response: any = yield call(getCategoryList, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(getCategoryListSuccess(response.data));
    } else {
      yield put(getCategoryListFailure(response.data));
    }
  } catch (error: any) {
    yield put(getCategoryListFailure(error.response));
  }
}

function* addCategorySaga(action: any): Generator {
  try {
    const response: any = yield call(addCategory, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(addCategorySuccess(response.data));
    } else {
      yield put(addCategoryFailure(response.data));
    }
  } catch (error: any) {
    yield put(addCategoryFailure(error.response));
  }
}

function* editCategorySaga(action: any): Generator {
  try {
    const response: any = yield call(editCategory, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(editCategorySuccess(response.data));
    } else {
      yield put(editCategoryFailure(response.data));
    }
  } catch (error: any) {
    yield put(editCategoryFailure(error.response));
  }
}

function* getFilterOptionsSaga(): Generator {
  try {
    // Make 3 parallel API calls for each filter type
    const [experienceResponse, ratingResponse, chargesResponse]: any[] = yield all([
      call(getFilterList, {type: FilterType.Experience}),
      call(getFilterList, {type: FilterType.Rating}),
      call(getFilterList, {type: FilterType.Charges}),
    ]);

    // Check if all responses are successful
    if (
      experienceResponse.data.status === SUCCESS_KEY &&
      ratingResponse.data.status === SUCCESS_KEY &&
      chargesResponse.data.status === SUCCESS_KEY
    ) {
      // Combine all filter responses into the expected format
      const combinedResponse = {
        status: SUCCESS_KEY,
        status_code: experienceResponse.data.status_code,
        data: {
          experience: experienceResponse.data.data.filters,
          rating: ratingResponse.data.data.filters,
          charge: chargesResponse.data.data.filters,
        },
      };
      yield put(getFilterOptionsSuccess(combinedResponse));
    } else {
      // If any call fails, use the first error response
      const errorResponse =
        experienceResponse.data.status !== SUCCESS_KEY
          ? experienceResponse.data
          : ratingResponse.data.status !== SUCCESS_KEY
          ? ratingResponse.data
          : chargesResponse.data;
      yield put(getFilterOptionsFailure(errorResponse));
    }
  } catch (error: any) {
    yield put(getFilterOptionsFailure(error.response || error));
  }
}

function* addFilterSaga(action: any): Generator {
  try {
    const response: any = yield call(addFilter, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(addFilterSuccess(response.data));
    } else {
      yield put(addFilterFailure(response.data));
    }
  } catch (error: any) {
    yield put(addFilterFailure(error.response));
  }
}

function* editFilterSaga(action: any): Generator {
  try {
    const response: any = yield call(editFilter, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(editFilterSuccess(response.data));
    } else {
      yield put(editFilterFailure(response.data));
    }
  } catch (error: any) {
    yield put(editFilterFailure(error.response));
  }
}

function* getDashboardConsultationsSaga(action: any): Generator {
  try {
    const response: any = yield call(getAdminPatientConsultations, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(getDashboardConsultationsSuccess(response.data));
    } else {
      yield put(getDashboardConsultationsFailure(response.data));
    }
  } catch (error: any) {
    yield put(getDashboardConsultationsFailure(error.response));
  }
}

function* getPopularSpecialistListSaga(action: any): Generator {
  try {
    const response: any = yield call(getPopularSpecialistList, action.payload);
    if (response?.data?.status === SUCCESS_KEY) {
      yield put(getPopularSpecialistListSuccess(response.data));
    } else {
      yield put(getPopularSpecialistListFailure(response?.data || {status_code: 'E-10044', status: 'error'}));
    }
  } catch (error: any) {
    const errorResponse = error?.response?.data || error?.data || {status_code: 'E-10044', status: 'error'};
    yield put(getPopularSpecialistListFailure(errorResponse));
  }
}

function* getPopularityLimitSaga(): Generator {
  try {
    const response: any = yield call(getPopularityLimit);
    if (response?.data?.status === SUCCESS_KEY) {
      yield put(getPopularityLimitSuccess(response.data));
    } else {
      yield put(getPopularityLimitFailure(response?.data || {status_code: 'E-10044', status: 'error'}));
    }
  } catch (error: any) {
    const errorResponse = error?.response?.data || error?.data || {status_code: 'E-10044', status: 'error'};
    yield put(getPopularityLimitFailure(errorResponse));
  }
}

function* updatePopularityLimitSaga(action: any): Generator {
  try {
    const response: any = yield call(updatePopularityLimit, action.payload);
    if (response?.data?.status === SUCCESS_KEY) {
      yield put(updatePopularityLimitSuccess(response.data));
    } else {
      yield put(updatePopularityLimitFailure(response?.data || {status_code: 'E-10044', status: 'error'}));
    }
  } catch (error: any) {
    const errorResponse = error?.response?.data || error?.data || {status_code: 'E-10044', status: 'error'};
    yield put(updatePopularityLimitFailure(errorResponse));
  }
}

function* overrideSpecialistPopularitySaga(action: any): Generator {
  try {
    const response: any = yield call(overrideSpecialistPopularity, action.payload);
    if (response?.data?.status === SUCCESS_KEY) {
      yield put(overrideSpecialistPopularitySuccess(response.data));
    } else {
      yield put(overrideSpecialistPopularityFailure(response?.data || {status_code: 'E-10044', status: 'error'}));
    }
  } catch (error: any) {
    console.error('Error overriding specialist popularity:', error);
    const errorResponse = error?.response?.data || error?.data || {status_code: 'E-10044', status: 'error'};
    yield put(overrideSpecialistPopularityFailure(errorResponse));
  }
}

function* getPatientAccountTicketsSaga(action: any): Generator {
  try {
    const response: any = yield call(getPatientAccountTickets, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(getPatientAccountTicketsSuccess(response.data));
    } else {
      yield put(getPatientAccountTicketsFailure(response.data));
    }
  } catch (error: any) {
    yield put(getPatientAccountTicketsFailure(error.response || error));
  }
}

function* exportPatientAccountSaga(action: any): Generator {
  try {
    const response: any = yield call(exportPatientAccount, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(exportPatientAccountSuccess(response.data));
    } else {
      yield put(exportPatientAccountFailure(response.data));
    }
  } catch (error: any) {
    yield put(exportPatientAccountFailure(error.response || error));
  }
}

function* deletePatientAccountSaga(action: any): Generator {
  try {
    const response: any = yield call(deletePatientAccount, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(deletePatientAccountSuccess(response.data));
    } else {
      yield put(deletePatientAccountFailure(response.data));
    }
  } catch (error: any) {
    yield put(deletePatientAccountFailure(error.response || error));
  }
}

function* holdPatientAccountDeletionSaga(action: any): Generator {
  try {
    const response: any = yield call(holdPatientAccountDeletion, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(holdPatientAccountDeletionSuccess(response.data));
    } else {
      yield put(holdPatientAccountDeletionFailure(response.data));
    }
  } catch (error: any) {
    yield put(holdPatientAccountDeletionFailure(error.response || error));
  }
}

function* resumePatientAccountDeletionSaga(action: any): Generator {
  try {
    const response: any = yield call(resumePatientAccountDeletion, action.payload);
    if (response.data.status === SUCCESS_KEY) {
      yield put(resumePatientAccountDeletionSuccess(response.data));
    } else {
      yield put(resumePatientAccountDeletionFailure(response.data));
    }
  } catch (error: any) {
    yield put(resumePatientAccountDeletionFailure(error.response || error));
  }
}

export default function* patientAdminSaga(): Generator {
  yield takeLatest(getCategoryListRequest.type, getCategoryListSaga);
  yield takeLatest(addCategoryRequest.type, addCategorySaga);
  yield takeLatest(editCategoryRequest.type, editCategorySaga);
  yield takeLatest(getFilterOptionsRequest.type, getFilterOptionsSaga);
  yield takeLatest(addFilterRequest.type, addFilterSaga);
  yield takeLatest(editFilterRequest.type, editFilterSaga);
  yield takeLatest(getDashboardConsultationsRequest.type, getDashboardConsultationsSaga);
  yield takeLatest(getPopularSpecialistListRequest.type, getPopularSpecialistListSaga);
  yield takeLatest(getPopularityLimitRequest.type, getPopularityLimitSaga);
  yield takeLatest(updatePopularityLimitRequest.type, updatePopularityLimitSaga);
  yield takeLatest(overrideSpecialistPopularityRequest.type, overrideSpecialistPopularitySaga);
  yield takeLatest(getPatientAccountTicketsRequest.type, getPatientAccountTicketsSaga);
  yield takeLatest(exportPatientAccountRequest.type, exportPatientAccountSaga);
  yield takeLatest(deletePatientAccountRequest.type, deletePatientAccountSaga);
  yield takeLatest(holdPatientAccountDeletionRequest.type, holdPatientAccountDeletionSaga);
  yield takeLatest(resumePatientAccountDeletionRequest.type, resumePatientAccountDeletionSaga);
}

